<script lang="ts">
  import { onMount } from "svelte"
  import Sortable from "sortablejs"
  import { Info, SquarePlus, CircleEllipsis, Copy, Trash2 } from "lucide-svelte"
  import { xhr, toast, sortElementsForSortable, wait } from "$app/utils"
  import { flip } from "svelte/animate"
  import EditGoalStatus from "./EditGoalStatus.svelte"
  import Expanded from "./Expanded.svelte"
  import Dropdown from "$app/components/Dropdown.svelte"
  import NewGoal from "./NewGoal.svelte"
  import Modal from "$app/components/Modal.svelte"
  import { duplicateGoal, deleteGoal } from "./methods"
  import { appStore } from "$app/store.svelte"
  import QuarterlyGoal from "$app/Pages/Plans/Goals/QuarterlyGoal.svelte"
  import { slide } from "svelte/transition"
  import Honeybadger from "$app/utils/honeybadger"

  interface Props {
    goal: GoalType
    pos?: number
    expanded?: boolean
  }

  let { goal, pos = 1, expanded = $bindable(false) }: Props = $props()

  const flipDurationMs = 300
  let openGoalDropdown = $state(false)
  let dragOver = $state(false)
  let selected = $state(false)
  let openNewGoalModal = $state(false)
  let subGoal: GoalType = $state({} as GoalType)

  function updateGoal(newGoal: GoalType): void {
    const idx = appStore.goals.findIndex((g) => g.id === goal.id)
    if (idx > -1) {
      appStore.goals[idx] = { ...goal, ...newGoal } as GoalType
    }
  }

  let goals: GoalType[] = $state([])

  let sortableSection = $state<HTMLElement | null>(null)

  function setSortable(): void {
    if (!sortableSection) return

    const sort = Sortable.create(sortableSection, {
      group: "QuarterlyGoals",
      animation: 200,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      dragClass: "border-green-400",
      onEnd: async (e: EventSortType) => {
        sort.options.sort = false
        await updateSort(e)
        sort.options.sort = true
      }
    })
  }

  async function updateSort(e: EventSortType) {
    const sameContainer =
      e.from.dataset.parent_goal_id === e.to.dataset.parent_goal_id

    if (e.newIndex === e.oldIndex && sameContainer) return

    const parentGoalId = parseInt(e.to.dataset.parent_goal_id)
    let newGoals: GoalType[] = []

    const currentGoals = appStore.goals
      .filter((g) => g.parent_goal_id === parentGoalId)
      .sort((a, b) => a.parent_position - b.parent_position)

    if (sameContainer) {
      newGoals = sortElementsForSortable(
        currentGoals,
        e.newIndex,
        e.oldIndex
      ) as GoalType[]
    } else {
      let tempGoals = appStore.quarterly_goals
        .filter((g) => g.parent_goal_id === parentGoalId)
        .sort((a, b) => a.parent_position - b.parent_position)

      newGoals = [
        ...tempGoals.slice(0, e.newIndex),
        currentGoals[e.oldIndex],
        ...tempGoals.slice(e.newIndex, tempGoals.length + 1)
      ]
    }

    newGoals = await saveSortOrder(newGoals, parentGoalId)

    newGoals.forEach((goal) => {
      const idx = appStore.goals.findIndex((g) => goal.id === g.id)
      appStore.goals[idx] = { ...appStore.goals[idx], ...goal }
    })

    goals = appStore.goals
      .filter((g) => g.parent_goal_id === goal.id)
      .sort((a, b) => a.parent_position - b.parent_position)
  }

  async function saveSortOrder(
    newGoals: GoalType[],
    parent_goal_id: number
  ): Promise<any> {
    try {
      const resp = await xhr.post(
        `/plans/${goal.plan_id}/goals/sort_by_parent.json`,
        { goals: newGoals.map((g) => g.id), id: parent_goal_id }
      )
      return resp.data.goals
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating the sort order", "error-")

      const tmpGoals = [...appStore.goals]
      appStore.goals = []
      await wait(10)
      appStore.goals = tmpGoals
      return false
    }
  }

  let mounted = $state(false)
  $effect(() => {
    if (mounted && expanded && appStore.editable) {
      setTimeout(() => {
        setSortable()
      }, 100)
    }
  })

  onMount(() => {
    goals = appStore.goals
      .filter((g) => g.parent_goal_id === goal.id)
      .sort((a, b) => a.parent_position - b.parent_position)

    mounted = true
  })
</script>

<div
  class="border-l-4 border-annual-goal shadow-sm flex ml-6 items-center relative p-2.5 select-none"
  class:border-2={dragOver}
  class:border-green-500={dragOver}
  class:bg-blue-100={selected}
  role="button"
  tabindex="0"
  ondragover={async (e) => {
    await wait(50)
    dragOver = appStore.draggedElement.type === "QuarterlyGoal"
  }}
  ondragleave={async () => {
    await wait(100)
    dragOver = false
  }}
  ondrop={async (e) => {
    if (appStore.draggedElement.type === "QuarterlyGoal") {
      appStore.dropElement = {
        ...appStore.dropElement,
        type: "AnnualGoal",
        id: goal.id,
        goal_id: 0
      }
    }
    await wait(50)
    dragOver = false
  }}
  ondblclick={() =>
    document.dispatchEvent(
      new CustomEvent("show-goal-detail", { detail: { goal } })
    )}
>
  {#if appStore.goals.filter((g) => g.parent_goal_id === goal.id).length > 0}
    <div class="absolute top-6" style="left:-14px">
      <Expanded
        class="text-annual-goal"
        bind:expanded
        data={{ name: "goal-expander" }}
      />
    </div>
  {/if}

  <div class="w-4/12 lg:w-5/12 text-left">
    <p class="text-ellipsis overflow-hidden">
      <span class="text-gray-400 text-sm">{pos}.</span>
      {goal.name}
    </p>
    <p class="text-xs text-gray-500">Annual Goal</p>
  </div>
  <div class="w-3/12 lg:w-2/12 text-center">
    <span
      class="inline-flex items-center rounded-md bg-zinc-50 px-2 py-1 text-xs font-medium text-zinc-600 ring-1 ring-inset ring-zinc-200"
    >
      {#if goal.assignee_name}
        {goal.assignee_name}
      {:else}
        Not Assigned
      {/if}
    </span>
  </div>
  <div class="w-4/12 lg:w-2/12 flex items-center justify-center">
    <EditGoalStatus
      goalOrTask={goal}
      type="Goal"
      small={true}
      editable={appStore.editable}
      show_border={false}
      onupdate={(data) => {
        updateGoal(data)
      }}
    />
  </div>
  <div class="hidden lg:inline-block w-2/12 text-center"></div>

  <div class="w-1/12 flex justify-end items-center">
    <Dropdown bind:open={openGoalDropdown}>
      {#snippet button()}
        <span
          class="rounded-full flex items-center justify-center focus:outline-none"
        >
          <CircleEllipsis class="text-gray-400 w-7 h-7" />
        </span>
      {/snippet}

      <div>
        <div
          class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 focus:outline-none z-10"
          role="menu"
        >
          <div class="py-1" role="none">
            <button
              onclick={() =>
                document.dispatchEvent(
                  new CustomEvent("show-goal-detail", { detail: { goal } })
                )}
              class="btn-dropdown"
              role="menuitem"
            >
              <Info class="text-gray-400 w-4 h-4 mr-3" />
              See Details
            </button>
            {#if appStore.editable}
              <button
                onclick={() => {
                  subGoal = {
                    type: "QuarterlyGoal",
                    parent_goal_id: goal.id,
                    parent_goal_type: "AnnualGoal",
                    expected_at: goal.expected_at,
                    assignee_id: goal.assignee_id,
                    assignee_type: goal.assignee_type,
                    assignee_name: goal.assignee_name
                  } as GoalType
                  openNewGoalModal = true
                  openGoalDropdown = false
                }}
                class="btn-dropdown"
                role="menuitem"
              >
                <SquarePlus class="text-gray-400 w-4 h-4 mr-3" />
                Add Sub-Goal
              </button>
              <button
                class="btn-dropdown"
                role="menuitem"
                onclick={() => {
                  duplicateGoal(goal)
                  openGoalDropdown = false
                }}
              >
                <Copy class="text-gray-400 w-4 h-4 mr-3" />
                Duplicate
              </button>
              <button
                class="btn-dropdown"
                role="menuitem"
                onclick={async () => {
                  selected = true
                  await deleteGoal(goal)
                  openGoalDropdown = false
                  selected = false
                }}
              >
                <Trash2 class="text-gray-400 w-4 h-4 mr-3" />
                Delete
              </button>
            {/if}
          </div>
        </div>
      </div>
    </Dropdown>
  </div>
</div>

{#if expanded}
  <div
    class="pl-8"
    bind:this={sortableSection}
    data-type="AnnualGoal"
    data-parent_goal_id={goal.id}
    transition:slide
  >
    {#key goals}
      {#each goals as goal, idx (goal.id)}
        <div
          animate:flip={{ duration: flipDurationMs }}
          class="ml-2"
          data-id={goal.id}
          data-type={goal.type}
        >
          <QuarterlyGoal goal={goals[idx]} pos={idx + 1} />
        </div>
      {/each}
    {/key}
  </div>
{/if}

<Modal bind:open={openNewGoalModal}>
  <NewGoal
    goal={subGoal}
    disableParent={true}
    disableType={true}
    oncreate={(g) => (openNewGoalModal = false)}
  />
</Modal>
